// Single source of truth for assembling the dashboard's COMPLETE filter-field set across every band.
//
// Historically this logic lived only inside the FilterModal hook (`useMyFilters`), which builds the
// recommendation-dynamic, building-dynamic, priority, and year bands and merges them into
// `dashboardReducer.filterFields` via `updateMasterFilter` — but ONLY when the modal is opened. The AI
// Filter Assistant ("Otto") reads `filterFields` too, so when the modal had never been opened it saw an
// incomplete set (e.g. it had `building_criticality` but NOT the real recommendation `criticality`), and
// resolved "high criticality" to the wrong, near-empty field. These builders are extracted here verbatim
// so BOTH the modal and Otto build the identical set, and Otto can assemble it on its own (no modal needed).

import { bands, bandPositions } from "./components/FilterModal/constants";
import { DYNAMIC_FIELD_TYPES } from "../common/constants";
import { recommendationCustomFieldBandKeyToFilterLabel } from "../../utils/recommendationBandUtils";

// BOOLEAN fields aren't enumerated in field_options (the backend sends a single empty placeholder), but
// they're always Yes/No. Real values confirmed from the dashboard's own /get_list
// (recommendation__immediate -> {id:"Yes"},{id:"No"}), so the apply matches what clicking the option stores.
const BOOLEAN_INLINE_OPTIONS = [
    { id: "Yes", name: "Yes" },
    { id: "No", name: "No" }
];

const isSelectOrBoolean = fieldType =>
    [DYNAMIC_FIELD_TYPES.SELECT, DYNAMIC_FIELD_TYPES.BOOLEAN].includes(fieldType);

export const isDateFieldType = fieldType => fieldType === "date" || fieldType === "date_time";

export const getSearchKeyFromField = field => {
    if (field?.searchKey) return field.searchKey;
    if (typeof field?.getListKey === "string") return field.getListKey;
    if (Array.isArray(field?.getListKey)) {
        return field.getListKey.find(key => key.includes(".")) || field.getListKey[field.getListKey.length - 1];
    }
    return null;
};

/** Redux / API key used when reading or writing filters.filters[wKey]. */
export const getWildcardStorageKey = field => {
    if (!field) return null;
    return field.wildcardParamKey || field.searchKey || getSearchKeyFromField(field) || field.paramKey;
};

/** Match a stored wildcard/top_n key to a filter field (paramKey vs searchKey/wildcardParamKey). */
export const fieldMatchesFilterWildcardKey = (field, key) => {
    if (!field || !key) return false;
    const keys = new Set([field.paramKey, field.wildcardParamKey, field.searchKey, getSearchKeyFromField(field)].filter(Boolean));
    return keys.has(key);
};

export const enrichFilterField = field => {
    if (!field) return field;
    const searchKey = getSearchKeyFromField(field) || (field.paramKey?.includes(".") ? field.paramKey : null);
    const wildcardOnly = isWildcardOnlyField(field);

    let getListKey = field.getListKey;
    if (!wildcardOnly && !getListKey && !field.options?.length && field.key !== "year") {
        if (searchKey) {
            getListKey = [searchKey];
        } else if (field.paramKey?.includes(".")) {
            getListKey = [field.paramKey];
        }
    }
    if (wildcardOnly) {
        getListKey = undefined;
    }

    const wildcardParamKey = field.wildcardParamKey || field.searchKey || searchKey || field.paramKey;

    let fieldType = field.fieldType;
    if (!fieldType) {
        if (field.isBoolean || field.options?.length || field.inlineOptions?.length) {
            fieldType = "select";
        } else {
            fieldType = "text";
        }
    }

    const inferredListField = !field.getListKey && getListKey;
    const shouldUseRecommendationApi =
        inferredListField &&
        field.isRecommendationField !== false &&
        (searchKey?.startsWith("recommendations.") ||
            searchKey?.startsWith("assets.") ||
            searchKey?.startsWith("buildings.") ||
            field.paramKey?.startsWith("recommendations.") ||
            field.paramKey?.startsWith("assets.") ||
            field.paramKey?.startsWith("buildings."));

    return {
        ...field,
        searchKey: searchKey || field.searchKey,
        getListKey,
        wildcardParamKey,
        fieldType,
        ...(shouldUseRecommendationApi ? { isRecommendationField: true } : {})
    };
};

export const enrichFilterFields = fields => (fields || []).map(enrichFilterField);

const resolveDynamicFieldType = field => {
    if ([DYNAMIC_FIELD_TYPES.NUMBER, DYNAMIC_FIELD_TYPES.CURRENCY].includes(field?.field_type)) return "number";
    if (field.field_type === DYNAMIC_FIELD_TYPES.MEMO) return "text";
    if (field.field_type === DYNAMIC_FIELD_TYPES.DATE) return "date";
    if (field.field_type === DYNAMIC_FIELD_TYPES.DATE_TIME) return "date_time";
    if (isSelectOrBoolean(field.field_type)) return "select";
    return field?.field_type;
};

export const isWildcardOnlyField = field => {
    if (!field) return false;
    if (field.key === "year") return false;
    if (field.isWildCardFilter) return true;
    const ft = field.fieldType;
    return ft === "date" || ft === "date_time";
};

export const hasListDropdown = field => {
    if (!field || isWildcardOnlyField(field)) return false;
    if (field.key === "year") return true;
    // Default for non-wildcard-only fields: show list dropdown (options may load lazily, e.g. FCI / color_codes).
    return true;
};

// Dynamic SELECT/BOOLEAN fields carry their options inline (field_options) rather than via a /get_list
// catalog — expose them so the resolver can match against them without a (meaningless) catalog fetch. For
// BOOLEAN fields we MUST substitute the real Yes/No pair: their field_options is one empty placeholder, so
// the resolver had only "" to match (e.g. "immediate" came back "no match for immediate").
const inlineOptionsOf = field => {
    if (field?.field_type === DYNAMIC_FIELD_TYPES.BOOLEAN) return BOOLEAN_INLINE_OPTIONS;
    return Array.isArray(field?.field_options)
        ? field.field_options.map(o => ({ id: o.value, name: o.label != null ? o.label : o.value }))
        : [];
};

// getListKey for inline-option (select/boolean) fields is ONLY an Otto apply lookup key — the raw
// field_options values COLLIDE across fields (booleans share an empty value; two selects can both be
// "Yes"), which would mis-map the apply to the wrong field's paramKey. Use the UNIQUE paramKey instead.
// FilterModal still lazy-loads dropdown options (with counts) via /get_list using inlineOptions ids.
const dynamicGetListKey = (field, paramKey) =>
    isSelectOrBoolean(field?.field_type) ? [paramKey] : undefined;

// Recommendation custom (dynamic) fields, grouped by band. Verbatim from useMyFilters effect1.
export const buildRecommendationDynamicFields = (recommendation_custom_fields, isCR) => {
    const isFieldVisible = field => {
        const vt = field.rules?.visible_type ?? "both";
        if (vt === "both") return true;
        return isCR ? vt === "cr_only" : vt === "recommendation_only";
    };
    const out = [];
    if (recommendation_custom_fields && Object.keys(recommendation_custom_fields).length) {
        Object.entries(recommendation_custom_fields).forEach(([band, item]) => {
            (item || []).filter(isFieldVisible).forEach((field, index) => {
                const isListField = isSelectOrBoolean(field.field_type);
                const paramKey = isListField
                    ? `recommendation_dynamic_field__${field?.field_name}`
                    : `recommendation_dynamic_fields.${field?.field_name}`;
                const wildcardParamKey = `recommendation_dynamic_fields.${field?.field_name}`;
                out.push({
                    label: field?.label,
                    key: field?.field_name,
                    paramKey,
                    wildcardParamKey,
                    exportFilterKey: paramKey,
                    band: recommendationCustomFieldBandKeyToFilterLabel(band),
                    bandPosition: [0, 1, 2].includes(index) ? bandPositions.header : bandPositions.body,
                    isRecommendationField: true,
                    getListKey: dynamicGetListKey(field, paramKey),
                    searchKey: `recommendation__${field?.field_name}`,
                    isDynamicField: true,
                    tab: "recommendation",
                    isWildCardFilter: !isListField,
                    isBoolean: field.field_type === DYNAMIC_FIELD_TYPES.BOOLEAN,
                    fieldType: resolveDynamicFieldType(field),
                    inlineOptions: inlineOptionsOf(field)
                });
            });
        });
    }
    return out;
};

// Building custom (dynamic) fields. Verbatim from useMyFilters effect (building_custom_field).
export const buildBuildingDynamicFields = building_custom_field => {
    const out = [];
    if (building_custom_field && Object.keys(building_custom_field).length) {
        Object.entries(building_custom_field).forEach(([band, item]) => {
            (item || []).forEach((field, index) => {
                const isListField = isSelectOrBoolean(field.field_type);
                const paramKey = isListField
                    ? `building_dynamic_field__${field?.field_name}`
                    : `building_dynamic_fields.${field?.field_name}`;
                const wildcardParamKey = `building_dynamic_fields.${field?.field_name}`;
                out.push({
                    label: field?.label,
                    key: field?.field_name,
                    paramKey,
                    wildcardParamKey,
                    exportFilterKey: paramKey,
                    band,
                    bandPosition: [0, 1, 2].includes(index) ? bandPositions.header : bandPositions.body,
                    isRecommendationField: true,
                    getListKey: dynamicGetListKey(field, paramKey),
                    searchKey: `building__${field?.field_name}`,
                    isDynamicField: true,
                    tab: "building",
                    isWildCardFilter: !isListField,
                    isBoolean: field.field_type === DYNAMIC_FIELD_TYPES.BOOLEAN,
                    fieldType: resolveDynamicFieldType(field),
                    inlineOptions: inlineOptionsOf(field)
                });
            });
        });
    }
    return out;
};

// Priority band: dynamic priority elements + the always-present Priority / Criticality / Criticality Type.
// Verbatim from useMyFilters effect (priorityElements).
export const buildPriorityFields = priorityElementsDropDownResponse => {
    let priorityElements = [];
    if (priorityElementsDropDownResponse?.priority_elements?.length) {
        priorityElementsDropDownResponse.priority_elements.forEach((field, i) => {
            priorityElements.push({
                label: field?.display_name || `Priority Element ${i + 1}`,
                key: `priority_elements.${field.id}`,
                paramKey: `option_ids.${i + 1}`,
                exportFilterKey: `priority_elements.${field.id}`,
                band: bands.priority,
                bandPosition: [0, 1, 2].includes(i) ? bandPositions.header : bandPositions.body,
                isRecommendationField: true,
                getListKey: [`priority_elements.${field.id}`],
                searchKey: `priority_elements.${field.id}`,
                wildcardParamKey: `priority_elements.${field.id}`,
                isPriority: true
            });
        });
    }
    priorityElements = [
        {
            label: "Priority",
            key: "priority",
            paramKey: "recommendations.priority",
            exportFilterKey: "recommendations.priority",
            band: bands.priority,
            bandPosition: bandPositions.body,
            searchKey: "recommendations.priority",
            wildcardParamKey: "recommendations.priority",
            isWildCardFilter: true,
            fieldType: "number",
            isRecommendationField: true
        },
        ...priorityElements,
        {
            label: "Criticality",
            key: "criticality",
            paramKey: "criticality_ids",
            exportFilterKey: "criticality_id",
            band: bands.priority,
            bandPosition: bandPositions.body,
            searchKey: "recommendations.criticality_name",
            isRecommendationField: true,
            getListKey: ["criticalities.id", "criticalities.name"]
        },
        {
            label: "Criticality Type",
            key: "criticality_type",
            paramKey: "criticality_type_ids",
            exportFilterKey: "criticality_type_ids",
            band: bands.priority,
            bandPosition: bandPositions.body,
            searchKey: "recommendations.criticality_type",
            isRecommendationField: true,
            getListKey: ["recommendations.criticality_type"]
        }
    ];
    return priorityElements;
};

// Capital-spending-plan band: one wildcard per plan year. Verbatim from useMyFilters effect (yearFilters).
export const buildYearFields = years => {
    const { start, end } = years || {};
    if (!start || !end) return [];
    return Array.from({ length: end - start + 1 }, (_, i) => start + i).map(year => {
        const yearKey = `maintenance_years.${year}`;
        return {
            label: year,
            key: year,
            paramKey: yearKey,
            wildcardParamKey: yearKey,
            searchKey: yearKey,
            band: bands.capital_spending_plan,
            bandPosition: bandPositions.body,
            isWildCardFilter: true,
            fieldType: "number",
            isRecommendationField: true
        };
    });
};

// Otto-facing relabels: the base set has building/site attributes whose labels COLLIDE with recommendation
// fields (building_criticality "Criticality" shadowed the real criticality). Disambiguate so the model can
// tell them apart. Applied only to Otto's copy — the dashboard UI labels are untouched.
//
// The numeric FCI/EFCI *metric* twins are a scope-pair collision too: the base set labels building_metrics_fci
// AND site_metrics_fci both "FCI" (and both EFCI twins "EFCI"), so a threshold pick like "fci over 0.2" would
// show an unqualified "FCI" chip with no way to tell building- from site-level. Prefix the scope so the chip
// reads "Building FCI" / "Site FCI". (The FCI/EFCI *Description* dropdowns — building_fci / site_fci / etc. —
// are disambiguated at their source in the filter-ai /schema, field_schema.py, not here.)
export const OTTO_FIELD_RELABELS = {
    building_criticality: "Building Criticality",
    building_status: "Building Status",
    building_country: "Building Country",
    site_country: "Site Country",
    building_state: "Building State",
    site_state: "Site State",
    building_metrics_fci: "Building FCI",
    site_metrics_fci: "Site FCI",
    building_metrics_efci: "Building EFCI",
    site_metrics_efci: "Site EFCI"
};

const keyOf = f => f && (f.key || f.paramKey);
const isPerYearField = f => /^maintenance_years\.\d+$/.test(f?.paramKey || "");

// NOTE (FCA-6735): the legacy project-level FCI filter (key `color_codes`, applies via `fci_color`) was
// REMOVED from the dashboard and replaced by per-band "FCI Description" / "EFCI Description" dropdowns
// (keys building_fci / site_fci / building_efci / site_efci — real isRecommendationField dimensions with
// their own getListKey). Those resolve through Otto's catalog like any other dimension: the filter-ai
// backend serves their band-name vocabulary (Excellent…Critical + not_zero) + ordinal glosses from
// /filter_catalog (see filter-ai catalog.py `_FCI_DESC_GET_LIST_FIELDS` + glosses.py). So no frontend
// enrichment is needed here anymore — the old inline-options `enrichFciField` is gone.

/**
 * The COMPLETE filter-field set for the assistant: the dashboard's base fields plus every band the
 * FilterModal builds (recommendation-dynamic, building-dynamic, priority), deduped by key (first wins)
 * and disambiguated. Per-year wildcard fields are dropped — the assistant handles plan years via
 * `year_range`, not as one toggle per year.
 */
export const assembleAllFilterFields = ({
    baseFilterFields = [],
    recommendation_custom_fields,
    building_custom_field,
    priorityElementsDropDownResponse,
    isCR = false
} = {}) => {
    const combined = [
        ...(baseFilterFields || []),
        ...buildRecommendationDynamicFields(recommendation_custom_fields, isCR),
        ...buildBuildingDynamicFields(building_custom_field),
        ...buildPriorityFields(priorityElementsDropDownResponse)
    ];
    const seen = new Set();
    const out = [];
    combined.forEach(f => {
        const k = keyOf(f);
        if (!k || seen.has(k) || isPerYearField(f)) return;
        seen.add(k);
        const relabeled = OTTO_FIELD_RELABELS[k] ? { ...f, label: OTTO_FIELD_RELABELS[k] } : f;
        out.push(enrichFilterField(relabeled));
    });
    return out;
};
